import { useState, useRef, useEffect } from 'react';

interface RefineTurn {
  role: 'user' | 'assistant';
  text: string;
}

interface RefinePanelProps {
  sectionId: string;
  sectionTitle: string;
  onSend: (message: string) => void;
  onClose: () => void;
  turns: RefineTurn[];
  streaming: boolean;
  streamingText: string;
  turnsUsed?: number;
  turnsMax?: number;
}

export function RefinePanel({ sectionTitle, onSend, onClose, turns, streaming, streamingText, turnsUsed, turnsMax }: RefinePanelProps) {
  const limitReached = turnsUsed != null && turnsMax != null && turnsUsed >= turnsMax;
  const [text, setText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns.length, streamingText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '0';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [text]);

  const handleSubmit = () => {
    if (text.trim().length === 0 || streaming) return;
    onSend(text.trim());
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-semibold tracking-wider uppercase text-placeholder">
          Refining {sectionTitle}
        </span>
        <button
          onClick={onClose}
          className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer border-none bg-transparent"
        >
          Close
        </button>
      </div>

      {turns.length > 0 && (
        <div className="space-y-3 mb-3 max-h-[300px] overflow-y-auto">
          {turns.map((turn, i) => (
            <div key={i} className={turn.role === 'user' ? 'pl-4 border-l-2 border-primary/30' : ''}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-placeholder mb-1">
                {turn.role === 'user' ? 'You' : 'The press'}
              </div>
              <p className={`text-sm leading-relaxed ${turn.role === 'assistant' ? 'font-display italic text-muted-foreground [font-variation-settings:\'opsz\'_48,\'SOFT\'_80]' : 'text-foreground'}`}>
                {turn.text}
              </p>
            </div>
          ))}
          {streaming && streamingText && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-placeholder mb-1">
                The press
              </div>
              <p className="text-sm leading-relaxed font-display italic text-muted-foreground [font-variation-settings:'opsz'_48,'SOFT'_80]">
                {streamingText}
                <span className="inline-block w-[2px] h-[1em] bg-primary/70 align-text-bottom animate-pulse ml-0.5" aria-hidden="true" />
              </p>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {limitReached ? (
        <div className="py-2 px-3 text-xs text-muted-foreground bg-background border border-border rounded-md text-center">
          {turnsUsed}/{turnsMax} refinements used
        </div>
      ) : (
        <div className="flex items-end gap-2 border border-border rounded-md bg-background focus-within:border-primary transition-colors duration-[80ms]">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming}
            placeholder="How would you like to change this?"
            rows={1}
            className="flex-1 bg-transparent py-2 pl-3 pr-2 text-sm leading-relaxed text-foreground resize-none min-h-[36px] max-h-[120px] focus:outline-none placeholder:text-placeholder disabled:opacity-50"
          />
          <div className="flex items-center gap-1.5 shrink-0 m-1">
            {turnsUsed != null && turnsMax != null && (
              <span className="text-[10px] text-placeholder">{turnsUsed}/{turnsMax}</span>
            )}
            <button
              disabled={streaming || text.trim().length === 0}
              onClick={handleSubmit}
              className="py-1.5 px-3 bg-primary text-white text-xs font-medium rounded-md disabled:opacity-30 cursor-pointer border-none"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
